import messages from "messages";
import { useCallback, useMemo } from "react";
import { useDrag } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import {
  SidesheetType,
  selectCurrentSidesheet,
  showGcalEventDetails,
} from "store/calendarSidebarSlice";
import { useCalendarSidebarContext } from "../../../../../calendar-sidebar/context/CalendarSidebarContext";
import {
  CalendarDndDropResult,
  CalendarDndItem,
  CalendarDndItemTypes,
} from "../../../../constants/calendarDndTypes";
import { CalendarColumnPosition } from "../../utils/splitEventsIntoColumns";
import TimeslotButton from "../timeslot-button/TimeslotButton";
import styles from "./GCalEventTimeslot.module.scss";

type Props = {
  value: CalendarBlockedInterval;
  date?: Date;
  daySpan?: number;
  calendarColumnPosition?: CalendarColumnPosition;
};

const GCalEventTimeslot = ({
  value,
  date,
  daySpan = 1,
  calendarColumnPosition,
}: Props) => {
  const dispatch = useDispatch();
  const { requestCancelAddNewSession } = useCalendarSidebarContext();

  const currentSidesheet = useSelector(selectCurrentSidesheet);

  const title = useMemo(
    () => value.busy_note || `(${messages.noTitle})`,
    [value.busy_note],
  );

  const showCurrentEventDetails = useCallback(
    () => dispatch(showGcalEventDetails(value)),
    [dispatch, value],
  );

  const onClick = useCallback(() => {
    if (
      currentSidesheet === SidesheetType.AddToCalendar &&
      requestCancelAddNewSession
    ) {
      requestCancelAddNewSession(showCurrentEventDetails);
    } else {
      showCurrentEventDetails();
    }
  }, [currentSidesheet, requestCancelAddNewSession, showCurrentEventDetails]);

  const [{ isAnotherAppointmentBeingDragged }] = useDrag<
    CalendarDndItem,
    CalendarDndDropResult,
    { isAnotherAppointmentBeingDragged: boolean }
  >(
    () => ({
      type: CalendarDndItemTypes.APPOINTMENT,
      collect: (monitor) => ({
        isAnotherAppointmentBeingDragged:
          monitor.getItemType() === CalendarDndItemTypes.APPOINTMENT,
      }),
    }),
    [],
  );

  return (
    <TimeslotButton
      className={styles["gcal-event-timeslot"]}
      onClick={onClick}
      style={{
        ...(isAnotherAppointmentBeingDragged ? { zIndex: 1 } : {}),
        ...(daySpan > 1
          ? { width: `calc(${daySpan * 100}% + ${(daySpan - 2) * 8}px)` }
          : {}),
      }}
      startDate={value.calendarStart || value.start}
      endDate={value.calendarEnd || value.end}
      timeslotName={title}
      date={date}
      showGCalIcon
      calendarColumnPosition={calendarColumnPosition}
    />
  );
};

export default GCalEventTimeslot;
